import {
  Box,
  Checkbox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { Channel } from 'lib-shared-types-channels';
import { useState } from 'react';

import { bansApi } from 'apis/bans';
import { useNotifications } from 'hooks/useNotifications';
import { channelAttributes } from 'utils/channels';

import styles from './banUserModal.module.scss';

type BanUserModalProps = {
  user: User;
  channel: Channel;
  csid?: number;
  ipAddress?: string;
  isOpen: boolean;
  onClose: (canceled: boolean) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractError(reason: any): string | null {
  const errors = reason?.data?.errors ?? [];
  if (errors.includes('ALREADY_BANNED')) {
    return 'Already banned';
  }

  return null;
}

export const BanUserModal = ({
  channel,
  csid,
  user,
  ipAddress,
  isOpen,
  onClose,
}: BanUserModalProps): JSX.Element => {
  const [shouldBanIp, setShouldBanIp] = useState(false);

  const [banUser, { isLoading: isBanUserLoading }] =
    bansApi.useUpdateUserBanMutation();
  const [banIp, { isLoading: isBanIpLoading }] =
    bansApi.useUpdateIpAddressBanMutation();

  const notify = useNotifications();

  const handleConfirm = async () => {
    ampli.bansBanUserClicked({
      ipChecked: shouldBanIp,
      channel,
    });

    if (user.type === 'member') {
      notify({
        title: 'Can not ban agent',
        text: `It's not possible to ban an agent.`,
        icon: 'exclamation',
        type: 'warning',
      });
      return;
    }

    try {
      await banUser({
        userId: user.id,
        channel,
        csid,
      }).unwrap();
    } catch (e) {
      notify({
        title: 'User ban error',
        text: extractError(e) ?? 'An error occurred when trying to ban user',
        icon: 'i-exclamation',
        className: 'danger',
      });

      return;
    }

    if (shouldBanIp && ipAddress) {
      try {
        await banIp({
          ipAddress,
          channel,
          csid,
        }).unwrap();
      } catch (e) {
        // Partial failure, close modal with refresh - further attempts to ban will fail because user is already banned with banUser request.
        onClose(false);
        notify({
          title: 'IP address ban error',
          text:
            extractError(e) ??
            'An error occurred when trying to ban IP address',
          icon: 'i-exclamation',
          className: 'warning',
        });

        return;
      }
    }

    onClose(false);

    notify({
      title: 'User banned',
      text: `User has been banned`,
      icon: 'i-done',
      className: 'success',
      time: 5000,
    });
  };

  const { name, email } = user;
  const channelName = channelAttributes[channel]?.name;
  const userIdentity = `${name || ''} ${email || ''}`.trim();

  const withCsidMessage = (
    <Text.Paragraph>
      Are you sure you want to ban user <strong>{userIdentity}</strong>? User
      will be unable to reply.
      {ipAddress && (
        <Box className={styles.ipBanContainer}>
          <Box as="span" className={styles.checkboxContainer}>
            <Checkbox
              name="shouldBanIp"
              onChange={() => setShouldBanIp((v) => !v)}
              checked={shouldBanIp}
            />
          </Box>
          <Text.Paragraph as="span">
            Also ban user IP address: <strong>{ipAddress}</strong>
          </Text.Paragraph>
        </Box>
      )}
    </Text.Paragraph>
  );
  const withoutCsidMessage = (
    <Text.Paragraph>
      Are you sure you want to ban user <strong>{name}</strong> on{' '}
      <strong>{channelName}</strong> channel?
    </Text.Paragraph>
  );
  const message = csid ? withCsidMessage : withoutCsidMessage;

  return (
    <Modal isOpen={isOpen} onClose={() => onClose(true)}>
      <ModalHead title="Are you sure?" onClose={() => onClose(true)} />
      <ModalBody>{message}</ModalBody>
      <ModalFoot
        cancelName="Cancel"
        cancelOnClick={() => onClose(true)}
        confirmName="Ban user"
        confirmProps={{
          variant: 'danger',
          isLoading: isBanUserLoading || isBanIpLoading,
          loadingIndicator: (
            <Text.Paragraph as="span" variant="3" wrap="nowrap" color="inherit">
              Banning...
            </Text.Paragraph>
          ),
        }}
        confirmOnClick={handleConfirm}
      ></ModalFoot>
    </Modal>
  );
};
